import { useRef, useState } from 'react';
import { Activity, ArrowUp, Check, ChevronDown, FileSpreadsheet, FileText, Folder, HardDrive, Link2, MessageSquare, Plus, RefreshCcw, Route as RouteIcon, Sparkles, X } from 'lucide-react';
import { activityCountFor, chatStoryFor, filesFor, linkedCountFor, pulsesFor } from '../../data/trackDetail';
import { GROUP_NAMES, TRACKS } from '../../data/workspaceTracks';
import { HUES } from '../alt/hues';
import { Glyph, Panel, Row, RowText } from '../dashboard/ui';
import { TrackActivityView, TrackLinkedView, TrackOverviewView } from './TrackDetailViews';
export type TrackSection = 'chat' | 'overview' | 'linked' | 'activity' | 'pulses' | 'drive';
type ThreadTab = {
  id: number;
  name: string;
  seeded: boolean;
  prompt?: string;
};
const SECTIONS: Array<{
  id: TrackSection;
  name: string;
  icon: typeof MessageSquare;
}> = [{
  id: 'chat',
  name: 'Chat',
  icon: MessageSquare
}, {
  id: 'overview',
  name: 'Overview',
  icon: Folder
}, {
  id: 'linked',
  name: 'Linked',
  icon: Link2
}, {
  id: 'activity',
  name: 'Activity',
  icon: Activity
}, {
  id: 'pulses',
  name: 'Pulses',
  icon: RefreshCcw
}, {
  id: 'drive',
  name: 'Drive',
  icon: HardDrive
}];

/**
 * A TRACK IS A WORKSPACE, NOT A CHATBOX.
 *
 * This component deliberately has no card shell, radius, outer border, inset
 * margin, or drop shadow. It is rendered as a full-height sibling of the chat
 * and workspace views inside Giga Chat, so its boundaries are the browser
 * edges and the shell's own navigation rail. The first implementation put a
 * rounded app inside the app and made the conversation look like a widget.
 *
 * The supplied reference uses a saturated blue track bar. We keep its useful
 * information architecture but use the standard neutral product stack from
 * index.css (--app-* roles): white chrome, near-white canvas, neutral
 * hairlines, near-black type, underline selection. The brand accent marks the
 * active view and the send action, and nothing else.
 */
export function TrackWorkspace({
  trackName,
  initialSection = 'chat',
  staticSection,
  exportMode = false
}: {
  trackName: string;
  /** Initial tab for Screens and direct links; interactive after mount. */
  initialSection?: TrackSection;
  /** Locks the workspace to one finished state for PNG/SVG capture. */
  staticSection?: TrackSection;
  /** Uses a finite artboard height instead of the browser viewport. */
  exportMode?: boolean;
}) {
  const track = TRACKS.find(item => item.name === trackName);
  const group = track?.group ?? GROUP_NAMES[0];
  const paletteKeys = ['indigo', 'teal', 'clay', 'plum', 'sky', 'amber', 'moss'] as const;
  const groupIndex = Math.max(0, GROUP_NAMES.indexOf(group));
  const groupHue = HUES[paletteKeys[groupIndex % paletteKeys.length]];
  const story = chatStoryFor(trackName);
  const trackPulses = pulsesFor(trackName);
  const [interactiveSection, setInteractiveSection] = useState<TrackSection>(initialSection);
  const section = staticSection ?? interactiveSection;
  const [threads, setThreads] = useState<ThreadTab[]>(() => story.threads.map((name, index) => ({
    id: index + 1,
    name,
    seeded: index === 0
  })));
  const [activeThread, setActiveThread] = useState(1);
  const nextThreadId = useRef(story.threads.length + 1);
  const [draft, setDraft] = useState('');
  const [modelOpen, setModelOpen] = useState(false);
  const [model, setModel] = useState<'Auto' | 'Giga Pro' | 'Claude Sonnet' | 'GPT-5'>('Auto');
  const active = threads.find(thread => thread.id === activeThread);
  const linkedCount = linkedCountFor(trackName);
  const activityCount = activityCountFor(trackName);
  const pulseCount = trackPulses.length;
  const addThread = () => {
    const id = nextThreadId.current++;
    setThreads(current => [...current, {
      id,
      name: 'New chat',
      seeded: false
    }]);
    setActiveThread(id);
    setDraft('');
    if (!staticSection) setInteractiveSection('chat');
  };
  const submitPrompt = (prompt: string) => {
    const nextPrompt = prompt.trim();
    if (!nextPrompt || !active) return;
    setThreads(current => current.map(thread => thread.id === active.id ? {
      ...thread,
      seeded: true,
      prompt: nextPrompt,
      name: nextPrompt.length > 34 ? `${nextPrompt.slice(0, 34)}…` : nextPrompt
    } : thread));
    setDraft('');
  };
  const removeThread = (id: number) => {
    setThreads(current => {
      const index = current.findIndex(thread => thread.id === id);
      const next = current.filter(thread => thread.id !== id);
      if (activeThread === id) setActiveThread(next[index - 1]?.id ?? next[0]?.id ?? -1);
      return next;
    });
  };
  return <div className={`flex w-full flex-col bg-app-surface text-app ${exportMode ? 'min-h-[680px]' : 'min-h-screen'}`} data-id="element-4063" data-mp="13qajdr7517h8">
      {/* One product toolbar, not a marketing headline stacked over navigation.
       The track name and its views are peers on the same plane. Colours are
       the semantic --app-* roles, so dark mode is one remap in index.css. */}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-app bg-app-surface px-4 md:px-6" data-id="element-4064" data-mp="3gvo12bxb7896">
        <div className="flex min-w-[180px] max-w-[280px] shrink-0 items-center gap-2.5 pr-3 md:border-r md:border-app" data-id="element-4065" data-mp="3bxmg1lgboyv2">
          <RouteIcon size={15} className="shrink-0 text-app-subtle" data-id="element-4066" data-mp="3pvazqpynlzyj" />
          <h1 className="truncate text-[14px] font-semibold text-app" data-id="element-4067" data-mp="3kz3rurmp6edy">{trackName}</h1>
        </div>
        <nav className="flex min-w-0 flex-1 self-stretch gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Track sections" data-id="element-4068" data-mp="2x9j660z5t30z">
          {SECTIONS.map(item => {
          const selected = item.id === section;
          const count = item.id === 'chat' ? threads.length : item.id === 'linked' ? linkedCount : item.id === 'activity' ? activityCount : item.id === 'pulses' ? pulseCount : null;
          return <button key={item.id} type="button" onClick={() => {
            if (!staticSection) setInteractiveSection(item.id);
          }} aria-pressed={selected} className={`relative flex shrink-0 items-center gap-1.5 px-3 text-[13px] font-medium transition-colors duration-200 ease-out after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 ${selected ? 'text-app after:bg-app-accent' : 'text-app-subtle after:bg-transparent hover:text-app'}`} data-id="element-4069" data-mp="29jf5o2h9pje7">
                <item.icon size={13} strokeWidth={2} data-id="element-4070" data-mp="s3vk2oif3gfs" />
                {item.name}
                {count !== null && <span className={`text-[11px] tabular-nums ${selected ? 'text-app-muted' : 'text-app-subtle'}`} data-id="element-4071" data-mp="3qodhy9y7z0jt">{count}</span>}
              </button>;
        })}
        </nav>
        <span className="hidden shrink-0 items-center gap-1.5 text-[12px] font-medium text-app-muted lg:flex" data-id="element-4072" data-mp="4fcjyw9iu9ys">
          <span className="size-1.5 rounded-full" style={{
          backgroundColor: groupHue.solid
        }} data-id="element-4073" data-mp="8i8udzowqzrn" />
          {group}
        </span>
      </header>

      {/* Open conversations belong to the Track, not only to the Chat view.
       Keeping this strip mounted means the team can inspect Overview or
       Activity without losing the conversations already in progress. */}
      <div className="flex h-11 shrink-0 items-center gap-1 overflow-x-auto border-b border-app bg-app-canvas px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:px-6" role="tablist" aria-label="Open track conversations" data-id="element-4074" data-mp="30d20vuv1uvwn">
        {threads.map(thread => {
        const selected = section === 'chat' && thread.id === activeThread;
        const openThread = () => {
          setActiveThread(thread.id);
          if (!staticSection) setInteractiveSection('chat');
        };
        return <div key={thread.id} role="tab" tabIndex={0} aria-selected={selected} onClick={openThread} onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openThread();
          }
        }} className={`group flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 text-[12px] font-medium transition-colors duration-200 ease-out ${selected ? 'bg-app-surface text-app shadow-[0_1px_2px_rgba(20,20,24,0.08)] ring-1 ring-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-4075" data-mp="2cn7plhqdpfei">
              <span className="max-w-[160px] truncate" data-id="element-4076" data-mp="3fxz70eiakkk5">{thread.name}</span>
              <button type="button" onClick={event => {
            event.stopPropagation();
            removeThread(thread.id);
          }} aria-label={`Close ${thread.name}`} className="text-app-subtle opacity-0 transition-[color,opacity] duration-150 ease-out hover:text-app group-hover:opacity-100 group-focus-within:opacity-100" data-id="element-4077" data-mp="3167451zooyzb">
                <X size={11} data-id="element-4078" data-mp="2w5khnnmbabnz" />
              </button>
            </div>;
      })}
        <button type="button" onClick={addThread} aria-label="New chat in this track" className="ml-1 flex size-7 shrink-0 items-center justify-center rounded-lg text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4079" data-mp="2i9yuzn3jw3yq">
          <Plus size={14} data-id="element-4080" data-mp="lgdqyhk7gsj0" />
        </button>
      </div>

      {section === 'chat' ? <div className="flex min-h-0 flex-1 flex-col" data-id="element-4081" data-mp="25xwj7cn1t90r">
          {/* Thread and composer are centred for reading, while their parent is
           full-width. That distinction is the important correction: readable
           content measure does not mean the PRODUCT itself is a card. */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-app-canvas px-5 py-7 md:px-8" data-id="element-4082" data-mp="2fpix5gqrtm77">
            {active?.seeded ? <div className="mx-auto flex max-w-3xl flex-col gap-5" data-id="element-4083" data-mp="2oo4ej8kiav6e">
                <div className="flex justify-end" data-id="element-4084" data-mp="2oqh70fnw4nxp">
                  <div className="max-w-[82%] rounded-2xl rounded-tr-md bg-app-text px-4 py-3 text-[15px] leading-relaxed text-white" data-id="element-4085" data-mp="2jq2uakbcgptc">
                    {active.prompt ?? story.userPrompt}
                  </div>
                </div>
                <div data-id="element-4086" data-mp="2to8w71x5hvy7">
                  <div className="mb-2.5 flex items-center gap-1.5 text-[12px] font-medium text-app-subtle" data-id="element-4087" data-mp="2tlmsjddds0l9">
                    <Sparkles size={12} className="text-app-accent" data-id="element-4088" data-mp="z9g59g77ptcl" />
                    Worked for {story.workTime} · {story.toolCalls} tool calls
                  </div>
                  <div className="max-w-[86%] text-[15px] leading-relaxed text-app" data-id="element-4089" data-mp="3tmmwc8ftivop">
                    {story.assistantIntro}
                  </div>
                </div>
                <div className="max-w-[86%] border-l-2 border-app-accent pl-4" data-id="element-4090" data-mp="3jrbhlq4etogy">
                  <h2 className="text-[14px] font-semibold text-app" data-id="element-4091" data-mp="3es8o92xsl6ot">{story.summaryTitle}</h2>
                  <ul className="mt-2 space-y-2 text-[14px] leading-relaxed text-app-muted" data-id="element-4092" data-mp="ga30x9lf4unt">
                    {story.highlights.map(highlight => <li key={highlight} data-id="element-4093" data-mp="bb8kgeaxbi6v">{highlight}</li>)}
                  </ul>
                </div>
              </div> : <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-end pb-8 text-center md:pb-12" data-id="element-4094" data-mp="6i71nv7liyi0">
                {/* Keep the empty Track chat visually connected to its composer.
                 The same heading scale and prompt pills are used on the main
                 welcome state, so starting a Track chat feels familiar. */}
                <div className="w-full" data-id="element-4095" data-mp="12j0c0560pu14">
                  <h2 className="text-[26px] font-semibold tracking-[-0.02em] text-app md:text-[30px]" data-id="element-4096" data-mp="12k1mo8j4geib">What should we work on?</h2>
                  <p className="mx-auto mt-3 max-w-[44ch] text-[15px] leading-relaxed text-app-muted" data-id="element-4097" data-mp="1biwv7r9n20os">
                    Ask about anything already linked to this Track.
                  </p>
                  <div className="mt-7 flex flex-wrap justify-center gap-2" data-id="element-4098" data-mp="1lh2vk4fsqyrj">
                    {story.starters.map(prompt => <button key={prompt} type="button" onClick={() => submitPrompt(prompt)} className="h-9 max-w-full truncate rounded-full border border-app bg-app-surface px-4 text-[13.5px] font-medium text-app-muted transition-[border-color,color] duration-200 ease-out hover:border-app-strong hover:text-app" data-id="element-4099" data-mp="1pig26gqpr5zi">
                        {prompt}
                      </button>)}
                  </div>
                </div>
              </div>}
          </div>

          <div className="shrink-0 bg-app-canvas px-5 py-4 md:px-8" data-id="element-4100" data-mp="297714hcjp8l6">
            <div className="mx-auto flex max-w-3xl items-end gap-2 rounded-2xl border border-app bg-app-surface px-4 py-3 shadow-[0_8px_30px_-20px_rgba(20,20,24,0.2)] focus-within:border-app-strong" data-id="element-4101" data-mp="1jjl1yw4es8jb">
              <button type="button" aria-label="Add attachment" className="flex size-7 shrink-0 items-center justify-center text-app-subtle transition-colors duration-200 ease-out hover:text-app" data-id="element-4102" data-mp="1t9ecdtstehlz">
                <Plus size={17} data-id="element-4103" data-mp="227zrfez2vei0" />
              </button>
              <textarea value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submitPrompt(draft);
            }
          }} rows={1} placeholder="Ask Giga about this track…" className="min-h-[24px] max-h-28 flex-1 resize-none bg-transparent text-[15px] text-app outline-none placeholder:text-app-subtle" data-id="element-4104" data-mp="22au94yqpil7q" />
              {/* Right-aligned, next to Send: it chooses what sends the
               message, so it belongs beside the send action itself. */}
              <span className="relative shrink-0" data-id="element-4105" data-mp="2tgjekgnrro2j">
                <button type="button" onClick={() => setModelOpen(open => !open)} aria-expanded={modelOpen} aria-haspopup="listbox" className={`inline-flex h-7 items-center gap-1 rounded-lg px-2 text-[12.5px] font-medium transition-colors duration-200 ease-out ${modelOpen ? 'bg-app-subtle text-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-4106" data-mp="2tf84jz3qdk2z">
                  {model}
                  <ChevronDown size={12} className={`text-app-subtle transition-transform duration-200 ${modelOpen ? 'rotate-180' : ''}`} data-id="element-4107" data-mp="29tlf9be4v5fd" />
                </button>
                {modelOpen && <>
                    <div className="fixed inset-0 z-30" onClick={() => setModelOpen(false)} data-id="element-4108" data-mp="20sx18goqp52h" />
                    <div role="listbox" aria-label="Choose model" className="absolute bottom-[calc(100%+8px)] right-0 z-40 w-[220px] overflow-hidden rounded-xl border border-app bg-app-surface p-1.5 shadow-[0_24px_60px_-24px_rgba(20,20,24,0.3)]" data-id="element-4109" data-mp="3q7ts2e93q0dt">
                      {(['Auto', 'Giga Pro', 'Claude Sonnet', 'GPT-5'] as const).map(option => <button key={option} type="button" role="option" aria-selected={model === option} onClick={() => {
                  setModel(option);
                  setModelOpen(false);
                }} className="flex h-10 w-full items-center justify-between rounded-lg px-3 text-left text-[13.5px] font-medium text-app-muted transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4110" data-mp="1q52cv4hj09at">
                          {option}
                          {model === option && <Check size={14} strokeWidth={2.5} className="text-app" data-id="element-4111" data-mp="12fzv7wmw6ggb" />}
                        </button>)}
                    </div>
                  </>}
              </span>
              <button type="button" onClick={() => submitPrompt(draft)} aria-label="Send" disabled={!draft.trim()} className={`flex size-9 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ease-out ${draft.trim() ? 'bg-app-text text-white hover:bg-black' : 'bg-app-subtle text-app-subtle'}`} data-id="element-4112" data-mp="tetbzfsuyrjd">
                <ArrowUp size={17} strokeWidth={2.4} data-id="element-4113" data-mp="350bzp0v3g1cs" />
              </button>
            </div>
            <p className="mt-2 text-center text-[11.5px] text-app-subtle" data-id="element-4114" data-mp="3a1rnh0onolyv">
              Giga can make mistakes. Check important information.
            </p>
          </div>
        </div> : <TrackSectionContent section={section} trackName={trackName} group={group} groupHue={groupHue} exportMode={exportMode} data-id="element-4115" data-mp="2hckltstwng9e" />}
    </div>;
}
function TrackSectionContent({
  section,
  trackName,
  group,
  groupHue,
  exportMode
}: {
  section: Exclude<TrackSection, 'chat'>;
  trackName: string;
  group: string;
  groupHue: {
    solid: string;
    tint: string;
    ink: string;
  };
  exportMode: boolean;
}) {
  const detailProps = {
    trackName,
    group,
    groupHue,
    exportMode
  };
  return <div className={`min-h-0 flex-1 overflow-y-auto bg-app-canvas px-5 md:px-8 ${exportMode ? 'py-5' : 'py-8'}`} data-id="element-4116" data-mp="22md279s5vr74">
      {section === 'overview' && <TrackOverviewView {...detailProps} data-id="element-4117" data-mp="35w7t7jv5qovx" />}
      {section === 'linked' && <TrackLinkedView {...detailProps} data-id="element-4118" data-mp="30xxreqexgq1c" />}
      {section === 'activity' && <TrackActivityView {...detailProps} data-id="element-4119" data-mp="3fnltvuyuhuqn" />}
      {section === 'pulses' && <TrackPulses trackName={trackName} data-id="element-4120" data-mp="3fpokag3wtpvw" />}
      {section === 'drive' && <TrackDrive trackName={trackName} data-id="element-4121" data-mp="3aq3nocc7ptmc" />}
    </div>;
}
function TrackPulses({
  trackName
}: {
  trackName: string;
}) {
  return <div className="mx-auto max-w-[900px]" data-id="element-4122" data-mp="2n1alvz8wsrjt">
      <div className="mb-5" data-id="element-4123" data-mp="2i0mz5prpqwve">
        <h2 className="text-[16px] font-semibold text-app" data-id="element-4124" data-mp="2i1epal2jk2ea">Work running on its own</h2>
        <p className="mt-1 text-[13.5px] text-app-muted" data-id="element-4125" data-mp="2w0xlcfi2vna8">Giga watches this track and delivers updates before the team has to ask.</p>
      </div>
      <Panel data-id="element-4126" data-mp="2s0nk1g58dl1p">
        {pulsesFor(trackName).map(pulse => <Row key={pulse.name} data-id="element-4127" data-mp="b8m40h9ccc66">
            <Glyph icon={RefreshCcw} data-id="element-4128" data-mp="b7u9zave6mi0" />
            <RowText title={pulse.name} meta={`${pulse.meta} · ${pulse.outcome}`} data-id="element-4129" data-mp="78bvoxy2t9o5" />
            <span className={`shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold ${pulse.status === 'Running' ? 'bg-app-accent-tint text-app-accent' : 'bg-app-subtle text-app-muted'}`} data-id="element-4130" data-mp="erkvq0kozw0j">{pulse.status}</span>
          </Row>)}
      </Panel>
    </div>;
}
function TrackDrive({
  trackName
}: {
  trackName: string;
}) {
  return <div className="mx-auto max-w-[900px]" data-id="element-4131" data-mp="9ryihr4v6frq">
      <div className="mb-5" data-id="element-4132" data-mp="9t04gqnf4mo8">
        <h2 className="text-[16px] font-semibold text-app" data-id="element-4133" data-mp="9ub7go91hqea">Files and deliverables</h2>
        <p className="mt-1 text-[13.5px] text-app-muted" data-id="element-4134" data-mp="xk5l3816nizl">Generated outputs and source files stay with the work that produced them.</p>
      </div>
      <Panel data-id="element-4135" data-mp="nt17abn8u46c">
        {filesFor(trackName).map(file => <Row key={file.name} data-id="element-4136" data-mp="nq6n8l856ldg">
            <Glyph icon={file.kind === 'spreadsheet' ? FileSpreadsheet : FileText} data-id="element-4137" data-mp="sr2rizweo9je" />
            <RowText title={file.name} meta={file.meta} data-id="element-4138" data-mp="1ggx1n9pcbjzy" />
          </Row>)}
      </Panel>
    </div>;
}